import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Share2, X } from "lucide-react";
import { toast } from "sonner";

const PHOTO_BACKGROUNDS = [
  { id: "cross-sunrise", src: "/share-bg/cross-sunrise.jpg", label: "Sunrise Cross" },
  { id: "light-rays", src: "/share-bg/light-rays.jpg", label: "Heavenly Light" },
  { id: "dove-sky", src: "/share-bg/dove-sky.jpg", label: "Dove at Dawn" },
  { id: "still-waters", src: "/share-bg/still-waters.jpg", label: "Still Waters" },
  { id: "wheat-field", src: "/share-bg/wheat-field.jpg", label: "Harvest Gold" },
  { id: "open-bible", src: "/share-bg/open-bible.jpg", label: "The Word" },
];
const CLASSIC_BG = { id: "classic", label: "Classic Parchment" };
const BACKGROUNDS = [...PHOTO_BACKGROUNDS, CLASSIC_BG];

const MS_PER_DAY = 86_400_000;

function dailyBackgroundIndex() {
  return Math.floor(Date.now() / MS_PER_DAY) % PHOTO_BACKGROUNDS.length;
}

const CARD_BG = "hsl(45 30% 96%)";
const CARD_PATTERN = { background: "radial-gradient(hsl(35 25% 80% / 0.4) 1px, transparent 1px)", backgroundSize: "20px 20px" };
const THEME_STYLE = { fontFamily: "Outfit, sans-serif", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 10, color: "hsl(210 10% 42%)" };
const VERSE_STYLE = { fontFamily: "Spectral, serif", fontStyle: "italic", fontSize: 24, lineHeight: 1.5, color: "hsl(150 15% 20%)", margin: "28px 0" };
const REF_STYLE = { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 600, color: "hsl(210 20% 12%)" };
const BRAND_ROW_STYLE = { marginTop: 36, display: "flex", alignItems: "center", gap: 8 };
const BRAND_DOT_STYLE = { width: 22, height: 22, borderRadius: 999, background: "hsl(150 15% 20%)", display: "inline-block" };
const BRAND_NAME_STYLE = { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 600, color: "hsl(150 15% 20%)" };

const SCRIM_STYLE = { position: "absolute", inset: 0, background: "linear-gradient(to top, hsl(160 20% 6% / 0.78) 0%, hsl(160 20% 6% / 0.25) 55%, hsl(160 20% 6% / 0.4) 100%)" };
const PHOTO_THEME_STYLE = { fontFamily: "Outfit, sans-serif", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 10, color: "hsl(38 45% 78%)" };
const PHOTO_VERSE_STYLE = { fontFamily: "Spectral, serif", fontStyle: "italic", fontSize: 22, lineHeight: 1.55, color: "hsl(45 40% 97%)", margin: "16px 0 14px", textShadow: "0 1px 12px hsl(160 20% 6% / 0.6)" };
const PHOTO_REF_STYLE = { fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 600, color: "hsl(45 40% 97%)", textShadow: "0 1px 10px hsl(160 20% 6% / 0.6)" };
const PHOTO_BRAND_DOT_STYLE = { width: 18, height: 18, borderRadius: 999, background: "hsl(45 40% 97%)", display: "inline-block" };
const PHOTO_BRAND_NAME_STYLE = { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 600, color: "hsl(45 40% 97%)" };

function ClassicCard({ verse, text }) {
  return (
    <div className="rounded-md overflow-hidden" style={{ background: CARD_BG }}>
      <div className="p-10 flex flex-col items-center text-center" style={CARD_PATTERN}>
        <p style={THEME_STYLE}>{verse.theme}</p>
        <p style={VERSE_STYLE}>"{text}"</p>
        <p style={REF_STYLE}>{verse.ref}</p>
        <div style={BRAND_ROW_STYLE}>
          <span style={BRAND_DOT_STYLE} />
          <span style={BRAND_NAME_STYLE}>Attune</span>
        </div>
      </div>
    </div>
  );
}

function PhotoCard({ bg, verse, text }) {
  return (
    <div className="relative rounded-md overflow-hidden aspect-[4/5]">
      <img src={bg.src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      <div style={SCRIM_STYLE} />
      <div className="absolute inset-0 flex flex-col items-center justify-end text-center p-8">
        <p style={PHOTO_THEME_STYLE}>{verse.theme}</p>
        <p style={PHOTO_VERSE_STYLE}>"{text}"</p>
        <p style={PHOTO_REF_STYLE}>{verse.ref}</p>
        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 7 }}>
          <span style={PHOTO_BRAND_DOT_STYLE} />
          <span style={PHOTO_BRAND_NAME_STYLE}>Attune</span>
        </div>
      </div>
    </div>
  );
}

function BackgroundPicker({ selected, onSelect }) {
  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {BACKGROUNDS.map((bg) => (
          <button key={bg.id} data-testid={`share-bg-thumb-${bg.id}`} onClick={() => onSelect(bg)}
            aria-label={bg.label} title={bg.label}
            className={`shrink-0 w-11 h-14 rounded-sm overflow-hidden border-2 transition-colors ${selected.id === bg.id ? "border-accent" : "border-transparent opacity-70 hover:opacity-100"}`}>
            {bg.src ? (
              <img src={bg.src} alt={bg.label} className="w-full h-full object-cover" />
            ) : (
              <span className="block w-full h-full" style={{ background: CARD_BG, ...CARD_PATTERN }} />
            )}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-1.5" data-testid="share-bg-label">{selected.label}</p>
    </div>
  );
}

export default function ShareVerseModal({ verse, translation, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [background, setBackground] = useState(PHOTO_BACKGROUNDS[dailyBackgroundIndex()]);
  const text = translation === "niv" ? verse.niv : verse.kjv;

  const generate = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await generate();
      const a = document.createElement("a");
      a.href = url;
      a.download = `attune-${verse.ref.replace(/[^a-z0-9]/gi, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Image saved.");
    } catch { toast.error("Could not create image."); }
    finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const url = await generate();
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], "attune-verse.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Attune", text: `"${text}" — ${verse.ref}` });
      } else {
        await download();
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Sharing not available; downloading instead.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-foreground/40 backdrop-blur-sm animate-fade-up" onClick={onClose} data-testid="share-modal">
      <div className="bg-background rounded-md max-w-md w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <p className="label-overline">Share this verse</p>
          <button data-testid="share-close-button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        {/* The card that becomes the image */}
        <div ref={cardRef}>
          {background.src
            ? <PhotoCard bg={background} verse={verse} text={text} />
            : <ClassicCard verse={verse} text={text} />}
        </div>

        <BackgroundPicker selected={background} onSelect={setBackground} />

        <div className="grid grid-cols-2 gap-3 mt-5">
          <button data-testid="share-native-button" onClick={share} disabled={busy}
            className="flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm py-3 hover:bg-primary/90 transition-colors disabled:opacity-60">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button data-testid="share-download-button" onClick={download} disabled={busy}
            className="flex items-center justify-center gap-2 border border-border rounded-sm py-3 hover:bg-muted transition-colors disabled:opacity-60">
            <Download className="w-4 h-4" /> Download
          </button>
        </div>
      </div>
    </div>
  );
}
